import React, { useEffect, useState } from "react";
import "./HeroShowcase.css";
import { apiFetch } from "../apiFetch";
import { getFlagCode } from "../utils/countryFlags";

const fallbackFeaturedPlayer = {
    fullName: "Calyweba Jr",
    country: "Italy",
    nationality: "Italy",
    dob: "2005-01-01",
    currentClub: "KYM FC",
    primaryPosition: "LW",
    secondaryPosition: "AM",
    preferredFoot: "Right",
    playerStrength: "Dribbling",
    status: "Available",
    displayImageUrl: "/images/Player-Dribbling.png",
    averageRating: 4,
    featuredReason: "FEATURED PROFILE",
};

const formatDob = (dob) => {
    if (!dob) return "Not Available";

    const date = new Date(dob);
    if (Number.isNaN(date.getTime())) return "Not Available";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
};

const getAge = (dob) => {
    if (!dob) return "Not Available";

    const birthDate = new Date(dob);
    if (Number.isNaN(birthDate.getTime())) return "Not Available";

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
        age -= 1;
    }

    return age;
};

const renderStars = (rating) => {
    const safeRating = Number(rating) || 0;
    const rounded = Math.max(0, Math.min(5, Math.round(safeRating)));

    return (
        <>
            {[...Array(5)].map((_, i) => (
                <span
                    key={i}
                    style={{
                        color: i < rounded ? "#ffd400" : "#d6d6d6",
                        marginRight: "1px"
                    }}
                >
                    ★
                </span>
            ))}
        </>
    );
};

const HeroShowcase = ({ player }) => {
    const isLoadingRealPlayer = player === null || player === undefined;
    const hasRealPlayerData = !!player && Object.keys(player).length > 0;

    const featuredPlayer = hasRealPlayerData
        ? player
        : fallbackFeaturedPlayer;

    const [averageRating, setAverageRating] = useState(null);
    const [ratingCount, setRatingCount] = useState(null);

    useEffect(() => {
        if (isLoadingRealPlayer || !featuredPlayer?.id) {
            setAverageRating(null);
            setRatingCount(null);
            return;
        }

        const fetchRating = async () => {
            try {
                const response = await apiFetch(`/api/player-average-rating/${featuredPlayer.id}`);

                if (!response.ok) {
                    const text = await response.text();
                    throw new Error(`Rating failed: ${response.status} ${text}`);
                }

                const data = await response.json();
                setAverageRating(parseFloat(data.averageRating));
                setRatingCount(data.ratingCount);
            } catch (err) {
                console.error("Error fetching showcase rating:", err);
                setAverageRating(null);
                setRatingCount(null);
            }
        };

        fetchRating();
    }, [isLoadingRealPlayer, featuredPlayer?.id]);

    if (isLoadingRealPlayer) {
        return null;
    }

    const playerName = featuredPlayer.fullName || "Featured Player";
    const playerCountry =
        featuredPlayer.nationality ||
        featuredPlayer.clubCountry ||
        featuredPlayer.country ||
        fallbackFeaturedPlayer.country;

    const playerFlagCode = playerCountry ? getFlagCode(playerCountry) : null;
    const playerDob = formatDob(featuredPlayer.dob);
    const playerAge = getAge(featuredPlayer.dob);
    const playerImage =
        featuredPlayer.displayImageUrl || fallbackFeaturedPlayer.displayImageUrl;
    const playerClub =
        featuredPlayer.currentClub || "Club Available";
    const playerPrimaryPosition =
        featuredPlayer.primaryPosition || "Not Available";
    const playerSecondaryPosition =
        featuredPlayer.secondaryPosition || "Not Available";
    const playerPreferredFoot =
        featuredPlayer.preferredFoot || "Not Available";
    const playerStrength =
        featuredPlayer.playerStrength || "Not Available";
    const playerStatus =
        featuredPlayer.status || "Available";
    const playerRatingStars = renderStars(
        averageRating !== null ? averageRating : featuredPlayer.averageRating
    );
    const featuredLabel =
        featuredPlayer.featuredReason || "FEATURED PROFILE";

    const showFetchedRatingCount =
        averageRating !== null && ratingCount !== null;

    return (
        <div className="hero-showcase">
            <div className="showcase-shell showcase-shell-back" />
            <div className="showcase-shell showcase-shell-mid" />

            <div className="showcase-main-card">
                <div className="showcase-topbar">
                    <div className="showcase-brand">
                        <img
                            src="/images/VSN-Icon-White-Cropped.png"
                            alt="VSN"
                            className="showcase-brand-logo"
                        />
                        <span>VIRTUAL SCOUTING NETWORK</span>
                    </div>

                    <div className="showcase-dots">
                        <span />
                        <span />
                        <span />
                    </div>
                </div>

                <div className="showcase-body">
                    <div className="showcase-side-column">
                        <article className="showcase-mini-card">
                            <div className="showcase-mini-image-wrap">
                                <img
                                    src="/images/scout-fallback.png"
                                    alt="Football Scouts"
                                    className="showcase-mini-image showcase-mini-image-person"
                                />
                            </div>

                            <div className="showcase-mini-content">
                                <h3>Scouts</h3>
                                <p>Discover talent anywhere with video-based player scouting.</p>
                            </div>
                        </article>

                        <article className="showcase-mini-card">
                            <div className="showcase-mini-image-wrap">
                                <img
                                    src="/images/agent-fallback.png"
                                    alt="Football Agents"
                                    className="showcase-mini-image showcase-mini-image-person showcase-mini-image-agent"
                                />
                            </div>

                            <div className="showcase-mini-content">
                                <h3>Agents</h3>
                                <p>Track, represent, and promote promising players worldwide.</p>
                            </div>
                        </article>

                        <article className="showcase-mini-card">
                            <div className="showcase-mini-image-wrap">
                                <img
                                    src="/images/club-fallback.png"
                                    alt="Professional Clubs"
                                    className="showcase-mini-image showcase-mini-image-logo"
                                />
                            </div>

                            <div className="showcase-mini-content">
                                <h3>Clubs</h3>
                                <p>Recruit rising stars faster with a global scouting network.</p>
                            </div>
                        </article>
                    </div>

                    <article className="showcase-player-card">
                        <div className="showcase-player-image-wrap">
                            <img
                                src={playerImage}
                                alt={playerName}
                                className="showcase-player-image"
                                onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = "/images/Player-Dribbling.png";
                                }}
                            />
                        </div>

                        <div className="showcase-player-details">
                            <div className="showcase-player-header">
                                <div>
                                    <p className="showcase-player-label">{featuredLabel}</p>

                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <h3 className="showcase-player-name">{playerName}</h3>

                                        {playerFlagCode && (
                                            <img
                                                src={`/flags/${playerFlagCode}.svg`}
                                                alt={playerCountry}
                                                title={playerCountry}
                                                style={{
                                                    width: "24px",
                                                    height: "18px",
                                                    objectFit: "contain",
                                                    display: "inline-block",
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="showcase-player-info">
                                <div className="showcase-player-row">
                                    <span>Date of Birth</span>
                                    <strong>{playerDob}</strong>
                                </div>

                                <div className="showcase-player-row">
                                    <span>Current Club</span>
                                    <strong>{playerClub}</strong>
                                </div>

                                <div className="showcase-player-row">
                                    <span>Primary Position</span>
                                    <strong>{playerPrimaryPosition}</strong>
                                </div>

                                <div className="showcase-player-row">
                                    <span>Secondary Position</span>
                                    <strong>{playerSecondaryPosition}</strong>
                                </div>

                                <div className="showcase-player-row">
                                    <span>Preferred Foot</span>
                                    <strong>{playerPreferredFoot}</strong>
                                </div>

                                <div className="showcase-player-row">
                                    <span>Strength</span>
                                    <strong>{playerStrength}</strong>
                                </div>
                            </div>

                            <div className="showcase-player-row">
                                <span>Age</span>
                                <strong>{playerAge}</strong>
                            </div>

                            <div className="showcase-player-row">
                                <span>Status</span>
                                <strong>{playerStatus}</strong>
                            </div>

                            <div className="showcase-player-rating">
                                <span>VSN Rating</span>

                                <strong>
                                    {playerRatingStars}

                                    {showFetchedRatingCount && (
                                        <span className="rating-count-text">
                                            ({ratingCount} ratings)
                                        </span>
                                    )}
                                </strong>
                            </div>
                        </div>
                    </article>
                </div>
            </div>

            <div className="showcase-flare" />
        </div>
    );
};

export default HeroShowcase;